import React, { useState } from 'react';
import { getAddFamilyMemberAvailability } from '../../../Services/UserFamily.service';
import {
	FamilyMember,
	useLazyFamilyMembersQuery,
	useSimpleUserQuery,
} from '../../../../../graphql/graphql';
import Loading from '../../../../BaseModule/Components/Loading';
import { useAppContext } from '../../../../../Services/AppContext';
import NetteApiClient from '../../../../../Services/NetteApiClient';
import Text from '../../../../BaseModule/Components/Text';
import { useEffectAsync } from '../../../../../helpers';

interface Props {
	userId: string;
}

const UserFamilyList: React.FC<Props> = ({ userId }) => {
	// @ts-ignore
	const {
		redirectUserProfile,
		redirectFamilyMemberProfile,
		redirectFamilyMemberInvitation,
		addFamilyMember,
	} = useAppContext();
	const [familyMembers, setFamilyMembers] = useState<
		FamilyMember[] | undefined
	>(undefined);
	const [isLoading, setIsLoading] = useState(false);
	const [showAddButton, setShowAddButton] = useState(false);
	const [getFamilyMembers] = useLazyFamilyMembersQuery();
	const { data: userData, isLoading: userIsLoading } = useSimpleUserQuery({
		id: userId,
	});

	useEffectAsync(async () => {
		setIsLoading(true);
		const familyMembers = await getFamilyMembers().unwrap();
		if (familyMembers) {
			setFamilyMembers(familyMembers?.familyMembers?.data as FamilyMember[]);
		}
		const { data } = await getAddFamilyMemberAvailability(userId);
		if (data) {
			setShowAddButton(data.adults || data.children);
		}
		setIsLoading(false);
	}, []);

	const handleRedirectFamilyMemberProfile = async (
		userId: string,
		familyMemberId: number,
	) => {
		console.log('redirectFamilyMemberProfile', redirectFamilyMemberProfile);
		await NetteApiClient.get(
			redirectFamilyMemberProfile
				.replace('__USER_ID__', userId)
				.replace('__FAMILY_MEMBER_ID__', familyMemberId),
		);
	};

	const handleRedirectUserProfile = async () => {
		await NetteApiClient.get(redirectUserProfile);
	};

	const handleAddFamilyMember = async () => {
		await NetteApiClient.get(addFamilyMember);
	};

	const handleRedirectFamilyMemberInvitation = async (
		userId: string,
		familyMemberId: number,
	) => {
		await NetteApiClient.get(
			redirectFamilyMemberInvitation
				.replace('__USER_ID__', userId)
				.replace('__FAMILY_MEMBER_ID__', familyMemberId),
		);
	};

	if (isLoading || userIsLoading) {
		return (
			<div className='d-flex justify-content-center'>
				<Loading />
			</div>
		);
	}

	return (
		<div className='row'>
			<div className='col-lg-4 col-6 mb-3'>
				<div
					className='family-member-card'
					onClick={() => handleRedirectUserProfile()}
				>
					<img
						className='family-member-card__photo'
						src={
							userData?.user?.profileImageUrl ??
							'/dist/placeholder/user-placeholder.png'
						}
					/>
					<div className='family-member-card__label'>
						{userData?.user?.profile?.firstName}
					</div>
				</div>
			</div>
			{familyMembers?.map(item => {
				const isInvitedMember = item?.role === 'INVITATION';
				return (
					<div className='col-lg-4 col-6 mb-3'>
						<div
							className='family-member-card'
							onClick={() =>
								item?.user?.id &&
								(isInvitedMember
									? handleRedirectFamilyMemberInvitation(
											item?.user?.id,
											item?.id,
									  )
									: handleRedirectFamilyMemberProfile(item?.user?.id, item?.id))
							}
						>
							<img
								className='family-member-card__photo'
								src={
									item?.user?.profileImageUrl ??
									'/dist/placeholder/user-placeholder.png'
								}
							/>
							<div className='family-member-card__label'>
								<div>
									{isInvitedMember
										? item?.user?.email
										: item?.user?.profile?.firstName}
								</div>
							</div>
							{isInvitedMember && (
								<Text
									className='family-member-card__badge'
									tx='base.invitation'
									firstUpper
								/>
							)}
						</div>
					</div>
				);
			})}
			{showAddButton && (
				<div className='col-lg-4 col-6 mb-3'>
					<div
						className='family-member-card'
						onClick={() => handleAddFamilyMember()}
					>
						<img
							className='family-member-card__plus mt-3'
							src='/dist/patientV3/icons/plus-purple.svg'
							alt='add'
						/>
						<Text
							className='family-member-card__label thin mt-3'
							tx='base.shared.add'
							firstUpper
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default UserFamilyList;
